import { supabase, supabaseReady } from "../../lib/supabaseClient";
import { ensureProfile } from "./ensureProfile";

/* Utilidades */

function resolveAppBase(): string {
  const envBase = (import.meta.env.VITE_APP_BASE_URL || "").trim();
  const base = envBase ? envBase.replace(/\/+$/, "") : window.location.origin.replace(/\/+$/, "");
  logDebug("resolveAppBase", base);
  return base;
}

function buildRedirect(path: string): string {
  const redirect = resolveAppBase() + (path.startsWith("/") ? path : `/${path}`);
  logDebug("buildRedirect", redirect);
  return redirect;
}

function logDebug(label: string, value: any) {
  if (import.meta.env.MODE === "development" && import.meta.env.VITE_VERBOSE_LOGS === "true") {
    // eslint-disable-next-line no-console
    console.log("[AUTH DEBUG]", label, value);
  }
}

export function validateEmail(email: string): { valid: boolean; message?: string } {
  const trimmed = email.trim().toLowerCase();
  
  // Verifica formato básico
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return { valid: false, message: 'Formato de email inválido.' };
  }
  
  // Verifica domínios bloqueados (emails de teste)
  const domain = trimmed.split('@')[1];
  const blockedDomains = ['test.com', 'teste.com', 'example.com', 'email.com', 'test.org'];
  if (blockedDomains.includes(domain)) {
    return { valid: false, message: 'Use um email real (ex: seunome@gmail.com).' };
  }
  
  return { valid: true };
}

export function classifyIdentity(input: string): "email" | "phone" | "invalid" {
  const trimmed = input.trim();
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return "email";
  if (/^\+?[1-9]\d{1,14}$/.test(trimmed.replace(/[\s()-]/g, ""))) return "phone";
  return "invalid";
}

/* Email + Senha */

export async function signUpEmailPassword(
  email: string,
  password: string,
  fullName: string,
  emailRedirectPath: string = "/oauth/callback", // Alterado para unificar verificação de perfil
) {
  if (!supabaseReady) throw new Error("Supabase não configurado");
  const emailRedirectTo = buildRedirect(emailRedirectPath);
  logDebug("signUpEmailPassword.redirectTo", emailRedirectTo);

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: { full_name: fullName },
      emailRedirectTo,
    },
  });

  if (error) throw error;
  // NÃO criar perfil aqui - usuário ainda não confirmou email
  // O perfil será criado no /oauth/callback após confirmação
  return data.user;
}

export async function signInEmailPassword(email: string, password: string) {
  if (!supabaseReady) throw new Error("Supabase não configurado");
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  if (data.user) await ensureProfile(data.user);
  return data.user;
}

/* OTP (Email / Telefone) */

export async function startOtp(
  identity: string,
  emailRedirectPath: string = "/oauth/callback",
): Promise<{ type: "email" | "phone" }> {
  if (!supabaseReady) throw new Error("Supabase não configurado");

  const kind = classifyIdentity(identity);
  if (kind === "invalid") throw new Error("E-mail ou telefone inválido");

  if (kind === "email") {
    const emailRedirectTo = buildRedirect(emailRedirectPath);
    logDebug("startOtp.emailRedirectTo", emailRedirectTo);
    const { error } = await supabase.auth.signInWithOtp({
      email: identity,
      options: { emailRedirectTo },
    });
    if (error) throw error;
    return { type: "email" };
  } else {
    const smsReady = !!import.meta.env.VITE_SMS_READY;
    if (!smsReady) {
      throw new Error("SMS não configurado. Use e-mail.");
    }
    const phone = identity.replace(/[\s()-]/g, "");
    const { error } = await supabase.auth.signInWithOtp({ phone });
    if (error) throw error;
    return { type: "phone" };
  }
}

export async function verifyPhoneOtp(phone: string, token: string) {
  if (!supabaseReady) throw new Error("Supabase não configurado");
  const cleanPhone = phone.replace(/[\s()-]/g, "");
  const { data, error } = await supabase.auth.verifyOtp({
    phone: cleanPhone,
    token,
    type: "sms",
  });
  if (error) throw error;
  if (data.user) await ensureProfile(data.user);
  return data.user;
}

/* OAuth Social */

export async function signInOAuth(provider: "google" | "facebook" | "apple", redirectPath: string = "/oauth/callback") {
  if (!supabaseReady) throw new Error("Supabase não configurado");
  
  const base = (import.meta.env.VITE_APP_BASE_URL || "").trim().replace(/\/+$/, "") || window.location.origin;
  const redirectTo = `${base}${redirectPath.startsWith("/") ? redirectPath : `/${redirectPath}`}`;
  
  logDebug("signInOAuth.provider", provider);
  logDebug("signInOAuth.redirectTo", redirectTo);
  
  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: { redirectTo },
  });
  if (error) throw error;
}

/* Sessão e Papéis */

export async function getSessionUser() {
  if (!supabaseReady) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.user ?? null;
}

/* Cache simples para evitar spam de consultas ao papel */
const roleCache: Record<string, { role: "admin" | "user" | null; timestamp: number }> = {};
const CACHE_TTL = 30000; // 30 segundos

/**
 * Helper para promessa com timeout resiliente
 */
async function withTimeout<T>(promise: Promise<T> | any, timeoutMs: number, errorMessage: string): Promise<T> {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error(errorMessage)), timeoutMs);
  });

  // Garante que o que estamos recebendo se comporte como uma Promise
  const actualPromise = Promise.resolve(promise);

  return Promise.race([actualPromise, timeoutPromise]);
}

export async function getUserRole(userId: string): Promise<"admin" | "user" | null> {
  if (!supabaseReady) return null;

  // 1. Verifica Cache
  const cached = roleCache[userId];
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.role;
  }
  
  try {
    logDebug("getUserRole.tableQuery", userId);
    
    // 2. Tenta Tabela com Timeout de 4s
    try {
      logDebug("getUserRole", "Consultando tabela user_roles...");
      const query = supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .maybeSingle();

      const result = (await withTimeout(query, 4000, "Timeout Tabela")) as any;

      if (result && !result.error && result.data) {
        const role = result.data.role as "admin" | "user";
        logDebug("getUserRole.tableFound", role);
        roleCache[userId] = { role, timestamp: Date.now() };
        return role;
      }
      if (result?.error) logDebug("getUserRole.tableError", result.error.message);
    } catch (tabErr: any) {
      logDebug("getUserRole.tableCatch", tabErr.message || tabErr);
    }

    // 3. Tenta RPC com Timeout de 3s
    logDebug("getUserRole", "Tentando fallback via RPC has_role...");
    
    try {
      const rpcAdmin = supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
      const isAdminRes = (await withTimeout(rpcAdmin, 3000, "Timeout RPC Admin")) as any;

      if (isAdminRes && !isAdminRes.error && isAdminRes.data) {
        logDebug("getUserRole.rpcFound", "admin");
        roleCache[userId] = { role: "admin", timestamp: Date.now() };
        return "admin";
      }

      const rpcUser = supabase.rpc("has_role", { _user_id: userId, _role: "user" });
      const isUserRes = (await withTimeout(rpcUser, 3000, "Timeout RPC User")) as any;

      if (isUserRes && !isUserRes.error && isUserRes.data) {
        logDebug("getUserRole.rpcFound", "user");
        roleCache[userId] = { role: "user", timestamp: Date.now() };
        return "user";
      }
    } catch (rpcErr: any) {
      logDebug("getUserRole.rpcCatch", rpcErr.message || rpcErr);
    }

    logDebug("getUserRole.notFound", userId);
    roleCache[userId] = { role: null, timestamp: Date.now() };
    return null;
  } catch (err: any) {
    console.error("[AUTH ERROR] getUserRole fail:", err.message || err);
    return null;
  }
}
